// src/services/api.ts
import axios from 'axios';
import { supabase } from './supabase';
import { setMaintenanceActive } from './maintenance';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: API_URL,
});

// Interceptor to add auth token, site context, and user headers
api.interceptors.request.use(async (config) => {
  // Attach Supabase JWT for backend validation
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch {
    // If session retrieval fails, let the request proceed without a token.
    // The backend will return 401 for protected endpoints, which the
    // response interceptor handles below.
  }

  const siteId = localStorage.getItem('currentSiteId');
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');

  if (siteId) {
    config.headers['X-Site-Id'] = siteId;
  }

  if (userId) {
    config.headers['X-User-Id'] = userId;
  }

  if (userEmail) {
    config.headers['X-User-Email'] = userEmail;
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Cutover maintenance mode (GCP migration §8): the old stack returns 503 with
    // { maintenance: true } for every route while frozen. Flip the whole SPA into
    // the full-page maintenance screen. Inert in normal operation (no 503s).
    if (error.response?.status === 503 && error.response?.data?.maintenance === true) {
      setMaintenanceActive(true);
    }

    // On 401, try refreshing the session and retry once.
    // This handles expired JWTs when the browser tab was inactive
    // and the Supabase auto-refresh timer didn't fire in time.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && data.session?.access_token) {
          originalRequest.headers['Authorization'] = `Bearer ${data.session.access_token}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed — fall through to clear context and reject
      }

      localStorage.removeItem('currentSiteId');
      localStorage.removeItem('userId');
      // Let the error propagate - React auth flow will handle redirects
    }

    // Handle site context errors
    if (error.response?.status === 400 && error.response?.data?.error?.includes('Site context')) {
      console.error('Site context required but not set');
      // Could redirect to site selection page
    }

    return Promise.reject(error);
  }
);

// Helper function to set site context
export const setSiteContext = (siteId: string | null) => {
  if (siteId) {
    localStorage.setItem('currentSiteId', siteId);
  } else {
    localStorage.removeItem('currentSiteId');
  }
};

// Helper function to set user context
export const setUserContext = (userId: string | null, userEmail?: string | null) => {
  if (userId) {
    localStorage.setItem('userId', userId);
  } else {
    localStorage.removeItem('userId');
  }
  if (userEmail) {
    localStorage.setItem('userEmail', userEmail);
  } else if (userEmail === null) {
    localStorage.removeItem('userEmail');
  }
};

// Helper function to get current site ID
export const getCurrentSiteId = (): string | null => {
  return localStorage.getItem('currentSiteId');
};

// Helper function to get current user ID
export const getCurrentUserId = (): string | null => {
  return localStorage.getItem('userId');
};

// Helper function to clear all context
export const clearContext = () => {
  localStorage.removeItem('currentSiteId');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
};

export default api;
